/**
 * shared/loginfx.ts —— 登录页的 Canvas 动效（2026-09-29 改版，设计稿「D+」）
 *
 * 画三样东西，全是纯装饰、不读任何业务数据（登录页是未登录态）：
 *   ① 光环：竖立的「门」+ 一段绕行亮弧 + 三条倾斜粒子环 + 星点；
 *      桌面端围着登录卡片（前半圈画在卡片之上的 front 画布，经过卡片时压暗），
 *      手机端（≤1000px）改为围着标题，只用 back 画布。
 *   ② 标题里的粒子 π：π 字仍在 DOM 里（透明），负责占位、字距和读屏；
 *      画布盖在它上面，粒子取样自同一字体串，与原字形重合。
 *   ③ 指针靠近 π 时粒子被推开，离开后弹回。
 *
 * ⚠ 为什么放在 shared/ 而不是页面里：登录页报错 / 切明文都会**整页重画**（`retryCurrent`），
 *   画布元素每次都是新的。引擎与粒子状态留在本模块，`mountLoginFx` 只负责接上新画布——
 *   否则每输错一次密码，π 都要重新飞一遍。
 * ⚠ 循环的启停不靠页面生命周期（页面契约没有 unmount）：每帧自检，
 *   画布脱离文档或 body 不再是 `.anon`（已登录）就停，重新进登录页时 `mountLoginFx` 再拉起来。
 * ⚠ reduced-motion：`15_media.css` 的全局兜底只管 CSS 动画，管不到 requestAnimationFrame。
 *   这里自己判：开了就只画一帧静态光环、不启粒子，π 回退成原来的渐变字（CSS 那边的兜底）。
 * ⚠ 颜色只用藏蓝五阶 + π 的暖橙三阶，与 `01_token.css` 同值；画布里取不到 var()，只能写数值。
 */

interface PiDot {
  hx: number; hy: number; x: number; y: number; vx: number; vy: number;
  seed: number; u: number; diag: number; size: number;
}
interface Ember { x: number; y: number; vx: number; vy: number; life: number }
interface Star { x: number; y: number; s: number; p: number; f: number }
interface RingDot { phi: number; a: number; lift: number }
interface Ring { r: number; e: number; sp: number; roll: number; dots: RingDot[] }
interface RingSpec { r: number; e: number; n: number; sp: number; roll: number }
interface Box { l: number; r: number; t: number; b: number }

const TAU = Math.PI * 2;
const MOBILE = '(max-width:1000px)';
const REDUCE = '(prefers-reduced-motion: reduce)';

/* 半径是 1440 / 390 宽下的设计值，运行时按视口宽度缩放；sp = 每秒转过的圈数 */
const DESK_RINGS: RingSpec[] = [
  { r: 380, e: 0.30, n: 260, sp: 1 / 110, roll: -0.07 },
  { r: 452, e: 0.34, n: 300, sp: -1 / 140, roll: 0.045 },
  { r: 540, e: 0.38, n: 360, sp: 1 / 175, roll: -0.025 },
];
const MOBILE_RINGS: RingSpec[] = [
  { r: 158, e: 0.22, n: 120, sp: 1 / 90, roll: -0.03 },
  { r: 188, e: 0.26, n: 140, sp: -1 / 120, roll: 0.025 },
  { r: 224, e: 0.30, n: 160, sp: 1 / 150, roll: -0.015 },
];

const S = {
  root: null as HTMLElement | null,
  back: null as HTMLCanvasElement | null,
  front: null as HTMLCanvasElement | null,
  pi: null as HTMLElement | null,
  piCv: null as HTMLCanvasElement | null,
  running: false,
  t0: 0,
  frame: 0,
  stars: [] as Star[],
  starKey: '',
  rings: [] as Ring[],
  ringKey: '',
  card: null as Box | null,
  head: null as Box | null,
  dots: null as PiDot[] | null,
  piKey: '',
  piBox: { cw: 0, ch: 0, fs: 0, gx: 0, gy: 0 },
  embers: [] as Ember[],
  px: null as number | null,
  py: 0,
  listening: false,
};

const mq = (q: string): boolean => typeof matchMedia === 'function' && matchMedia(q).matches;
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** 登录页 `mounted()` 里调用：接上（新的）画布，必要时拉起循环 */
export function mountLoginFx(root: HTMLElement | null): void {
  if (!root) return;
  S.root = root;
  S.back = root.querySelector<HTMLCanvasElement>('.lg-fx-back');
  S.front = root.querySelector<HTMLCanvasElement>('.lg-fx-front');
  S.pi = root.querySelector<HTMLElement>('.lg-hero .pi');
  S.piCv = root.querySelector<HTMLCanvasElement>('.pi-fx');
  S.card = null;
  S.piKey = '';
  if (!S.back?.getContext('2d')) return; // 画布不可用：页面保持静态，π 维持渐变字

  if (!S.listening) {
    S.listening = true;
    /* 全站只挂一次（不是每次渲染都挂）：指针位置只给粒子 π 用 */
    window.addEventListener('pointermove', (e) => { S.px = e.clientX; S.py = e.clientY; }, { passive: true });
    window.addEventListener('pointerdown', (e) => { S.px = e.clientX; S.py = e.clientY; }, { passive: true });
    window.addEventListener('pointerup', (e) => { if (e.pointerType !== 'mouse') S.px = null; }, { passive: true });
    document.documentElement.addEventListener('pointerleave', () => { S.px = null; });
    window.addEventListener('resize', () => { if (mq(REDUCE) && alive()) drawFrame(performance.now(), true); });
  }

  if (mq(REDUCE)) {
    drawFrame(performance.now(), true);
    return;
  }
  if (!S.running) {
    S.running = true;
    S.t0 = performance.now();
    requestAnimationFrame(loop);
  }
}

function alive(): boolean {
  return !!S.back?.isConnected && document.body.classList.contains('anon');
}

function loop(now: number): void {
  if (!alive()) { S.running = false; return; }
  drawFrame(now, false);
  requestAnimationFrame(loop);
}

function prep(cv: HTMLCanvasElement, W: number, H: number): CanvasRenderingContext2D | null {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = Math.round(W * dpr), h = Math.round(H * dpr);
  if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
  const c = cv.getContext('2d');
  if (!c) return null;
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  c.clearRect(0, 0, W, H);
  return c;
}

/** 元素相对 .login 的盒子（画布铺满 .login，坐标系一致） */
function boxOf(el: Element | null): Box | null {
  const root = S.root;
  if (!el || !root) return null;
  const rr = root.getBoundingClientRect(), er = el.getBoundingClientRect();
  const k = rr.width / (root.clientWidth || 1) || 1;
  return { l: (er.left - rr.left) / k, r: (er.right - rr.left) / k, t: (er.top - rr.top) / k, b: (er.bottom - rr.top) / k };
}

function drawFrame(now: number, still: boolean): void {
  const back = S.back, front = S.front, root = S.root;
  if (!back || !front || !root) return;
  const W = back.clientWidth, H = back.clientHeight;
  if (!W || !H) return;
  const t = still ? 5 : (now - S.t0) / 1000;
  const B = prep(back, W, H), F = prep(front, W, H);
  if (!B || !F) return;
  const mobile = mq(MOBILE);

  /* 尺寸一变（窗口拉伸 / 转屏 / 切表单撑高了页面）立即重测，否则每 15 帧补测一次 */
  const starKey = W + 'x' + H;
  const resized = S.starKey !== starKey;
  if (resized || !S.card || S.frame++ % 15 === 0) {
    S.card = boxOf(root.querySelector('.lg-box'));
    S.head = boxOf(root.querySelector('.lg-hd'));
  }

  if (resized) {
    S.starKey = starKey;
    const n = Math.round(clamp((W * H) / 4200, 90, 320));
    S.stars = Array.from({ length: n }, () => ({
      x: Math.random() * W, y: Math.random() * H, s: Math.random() < 0.9 ? 0.8 : 1.6,
      p: Math.random() * TAU, f: 0.4 + Math.random() * 1.2,
    }));
  }
  const ringKey = mobile ? 'm' : 'd';
  if (S.ringKey !== ringKey) {
    S.ringKey = ringKey;
    S.rings = (mobile ? MOBILE_RINGS : DESK_RINGS).map((g) => ({
      r: g.r, e: g.e, sp: g.sp, roll: g.roll,
      dots: Array.from({ length: g.n }, (_, i) => ({
        phi: (i / g.n) * TAU + (Math.random() - 0.5) * 0.02, a: 0.4 + Math.random() * 0.6,
        lift: (Math.random() - 0.5) * (mobile ? 3 : 6),
      })),
    }));
  }

  /* 几何：桌面围着卡片，手机围着标题（竖门圈住整个标题块，斜环中心压在标题下沿，
     前半圈从英文小字与卡片之间穿过，不压字） */
  let cx: number, cy: number, ry: number, PR: number, k: number;
  if (mobile) {
    const h = S.head;
    k = clamp(W / 390, 0.9, 1.35);
    cx = W / 2;
    cy = h ? (h.t + h.b) / 2 : 142;
    ry = h ? h.b - 2 : 188;
    PR = 160 * k;
  } else {
    const c = S.card;
    k = clamp(W / 1440, 0.85, 1.2);
    cx = c ? (c.l + c.r) / 2 : W / 2;
    cy = c ? (c.t + c.b) / 2 : H / 2;
    ry = cy + 33;
    PR = c ? Math.max(300, Math.hypot((c.r - c.l) / 2, (c.b - c.t) / 2) + 14) : 300;
  }

  const g = B.createRadialGradient(cx, cy, 0, cx, cy, mobile ? 220 * k : 420);
  g.addColorStop(0, 'rgba(92,95,140,.40)');
  g.addColorStop(1, 'rgba(92,95,140,0)');
  B.fillStyle = g;
  B.fillRect(0, 0, W, H);

  for (const s of S.stars) {
    B.fillStyle = 'rgba(222,223,236,' + (0.15 + 0.35 * (0.5 + 0.5 * Math.sin(t * s.f + s.p))) + ')';
    B.fillRect(s.x, s.y, s.s, s.s);
  }

  /* 竖门：细环 + 虚线外圈 + 一段绕行亮弧（14s 一圈，另一端半亮） */
  B.lineWidth = 1;
  B.beginPath(); B.strokeStyle = 'rgba(184,186,212,.16)'; B.arc(cx, cy, PR, 0, TAU); B.stroke();
  B.beginPath(); B.setLineDash([1.5, mobile ? 7 : 9]); B.lineDashOffset = -t * 5;
  B.strokeStyle = 'rgba(184,186,212,.25)'; B.arc(cx, cy, PR + (mobile ? 14 : 22), 0, TAU); B.stroke();
  B.setLineDash([]);
  if (!mobile) {
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU;
      B.beginPath();
      B.moveTo(cx + Math.cos(a) * (PR + 8), cy + Math.sin(a) * (PR + 8));
      B.lineTo(cx + Math.cos(a) * (PR + 14), cy + Math.sin(a) * (PR + 14));
      B.strokeStyle = 'rgba(222,223,236,.35)'; B.stroke();
    }
  }
  const head = (t * TAU) / 14 - Math.PI / 2, seg = mobile ? 0.06 : 0.045, segs = mobile ? 24 : 28;
  for (const [hd, amt] of [[head, 1], [head + Math.PI, 0.45]] as const) {
    for (let j = 0; j < segs; j++) {
      B.beginPath(); B.arc(cx, cy, PR, hd - (j + 1) * seg, hd - j * seg);
      B.lineWidth = mobile ? 1.8 : 2;
      B.strokeStyle = 'rgba(222,223,236,' + 0.8 * amt * (1 - j / segs) + ')'; B.stroke();
    }
    B.save(); B.shadowColor = 'rgba(222,223,236,.95)'; B.shadowBlur = mobile ? 12 : 16;
    B.beginPath(); B.arc(cx + Math.cos(hd) * PR, cy + Math.sin(hd) * PR, 2.6 * amt + 0.6, 0, TAU);
    B.fillStyle = 'rgba(245,246,252,' + amt + ')'; B.fill(); B.restore();
  }

  /* 斜环：粒子公转，三处「数据包」亮点沿环走；桌面后半圈在卡片后、前半圈在卡片前 */
  const card = mobile ? null : S.card;
  S.rings.forEach((ring, ri) => {
    const r = ring.r * k, psi = t * TAU * ring.sp, cr = Math.cos(ring.roll), sr = Math.sin(ring.roll);
    const packs = (mobile ? [0, 1] : [0, 1, 2]).map((q, _, arr) => (q * TAU) / arr.length + t * 0.35 * (ri % 2 ? -1 : 1) + ri);
    for (const p of ring.dots) {
      const a = p.phi + psi, x = r * Math.cos(a), z = r * Math.sin(a);
      const depth = (z / r + 1) / 2;
      const X = x * (1 + (z / r) * 0.06), Y = z * ring.e + p.lift;
      const sx = cx + X * cr - Y * sr, sy = ry + X * sr + Y * cr;
      let b = 0;
      for (const q of packs) {
        let d = Math.abs((((p.phi - q) % TAU) + TAU) % TAU);
        d = Math.min(d, TAU - d);
        b = Math.max(b, Math.exp(-(d * d) / (mobile ? 0.015 : 0.012)));
      }
      let alpha = (mobile ? (0.1 + 0.4 * depth) : (0.12 + 0.5 * depth)) * p.a + b * 0.75;
      const toFront = !mobile && z > 0;
      if (toFront && card && sx > card.l - 10 && sx < card.r + 10 && sy > card.t - 10 && sy < card.b + 10) alpha *= 0.28;
      const ctx = toFront ? F : B;
      ctx.fillStyle = 'rgba(' + Math.round(184 + 60 * b) + ',' + Math.round(186 + 60 * b) + ',' + Math.round(212 + 40 * b) + ',' + Math.min(1, alpha) + ')';
      ctx.beginPath(); ctx.arc(sx, sy, (mobile ? 0.6 + 0.9 * depth + b * 1.1 : 0.7 + 1.1 * depth + b * 1.4), 0, TAU); ctx.fill();
    }
  });

  if (!still) drawPi(t);
}

/* ---------- 粒子 π ---------- */

function buildPi(): boolean {
  const el = S.pi, cv = S.piCv;
  if (!el || !cv) return false;
  const bw = el.offsetWidth, bh = el.offsetHeight;
  if (!bw || !bh) return false;
  const cs = getComputedStyle(el), fs = parseFloat(cs.fontSize), padL = parseFloat(cs.paddingLeft) || 0;
  const PAD = Math.round(fs * 0.9), cw = bw + PAD * 2, ch = bh + PAD * 2;
  cv.style.left = -PAD + 'px'; cv.style.top = -PAD + 'px';
  cv.style.width = cw + 'px'; cv.style.height = ch + 'px';

  const key = bw + 'x' + bh + '@' + fs;
  if (S.dots && S.piKey === key) { el.classList.add('on'); return true; } // 重画后接回：粒子原样沿用

  const SC = 4, off = document.createElement('canvas');
  off.width = Math.ceil(cw * SC); off.height = Math.ceil(ch * SC);
  const o = off.getContext('2d');
  if (!o) return false;
  o.scale(SC, SC);
  o.font = 'italic 700 ' + fs + 'px Arial, "Helvetica Neue", Helvetica, sans-serif';
  o.textBaseline = 'alphabetic';
  const asc = o.measureText('π').fontBoundingBoxAscent || fs * 0.905;
  o.fillStyle = '#fff';
  o.fillText('π', PAD + padL, PAD + asc);
  const d = o.getImageData(0, 0, off.width, off.height).data;
  const step = Math.max(1.2, fs / 38), raw: Array<[number, number]> = [];
  let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
  for (let y = 0; y < ch; y += step) {
    for (let x = 0; x < cw; x += step) {
      if ((d[(Math.floor(y * SC) * off.width + Math.floor(x * SC)) * 4 + 3] ?? 0) > 120) {
        raw.push([x, y]);
        x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
      }
    }
  }
  if (!raw.length) return false;
  const gx = (x0 + x1) / 2, gy = (y0 + y1) / 2, intro = !S.dots;
  S.dots = raw.map(([x, y]) => {
    const a = Math.random() * TAU, rr = fs * (0.5 + Math.random() * 0.9);
    return {
      hx: x, hy: y, x: intro ? gx + Math.cos(a) * rr : x, y: intro ? gy + Math.sin(a) * rr : y, vx: 0, vy: 0,
      seed: Math.random(), u: (y - y0) / (y1 - y0 || 1),
      diag: ((x - x0) / (x1 - x0 || 1) + (y - y0) / (y1 - y0 || 1)) / 2,
      size: (fs / 60) * (0.75 + Math.random() * 0.5),
    };
  });
  S.embers = [];
  S.piKey = key;
  S.piBox = { cw, ch, fs, gx, gy };
  el.classList.add('on'); // 粒子就绪才把原字设透明；失败时渐变字一直在
  return true;
}

function drawPi(t: number): void {
  const el = S.pi, cv = S.piCv;
  if (!el || !cv) return;
  const key = el.offsetWidth + 'x' + el.offsetHeight + '@' + parseFloat(getComputedStyle(el).fontSize);
  if (!S.dots || S.piKey !== key || !el.classList.contains('on')) { if (!buildPi()) return; }
  const dots = S.dots;
  if (!dots) return;
  const { cw, ch, fs, gx, gy } = S.piBox;
  const ctx = prep(cv, cw, ch);
  if (!ctx) return;

  let mx = -9999, my = -9999;
  if (S.px !== null) {
    const r = cv.getBoundingClientRect(), k = r.width / cw || 1;
    mx = (S.px - r.left) / k; my = (S.py - r.top) / k;
  }
  const g = ctx.createRadialGradient(gx, gy, 0, gx, gy, fs * 0.95);
  g.addColorStop(0, 'rgba(226,106,46,' + 0.2 * (0.8 + 0.2 * Math.sin(t * 1.4)) + ')');
  g.addColorStop(1, 'rgba(226,106,46,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, cw, ch);

  ctx.globalCompositeOperation = 'lighter';
  const R = fs * 0.72, wob = fs / 110, band = ((t * 0.18) % 1.6) - 0.3;
  for (const p of dots) {
    const tx = p.hx + Math.sin(t * 0.9 + p.seed * TAU) * wob;
    const ty = p.hy + Math.cos(t * 0.8 + p.seed * TAU) * wob + Math.sin(t * 1.3 - p.hx * 0.08) * wob * 0.7;
    let ax = (tx - p.x) * 0.07, ay = (ty - p.y) * 0.07;
    const dx = p.x - mx, dy = p.y - my, d2 = dx * dx + dy * dy;
    if (d2 < R * R && d2 > 0.01) {
      const dd = Math.sqrt(d2), f = (1 - dd / R) * fs / 22;
      ax += (dx / dd) * f; ay += (dy / dd) * f;
    }
    p.vx = (p.vx + ax) * 0.84; p.vy = (p.vy + ay) * 0.84;
    p.x += p.vx; p.y += p.vy;
    /* 渐变 = 品牌 π 的 --pi-1 → --pi-3；斜向扫光只提亮度 */
    const b = Math.exp(-Math.pow(p.diag - band, 2) / 0.008);
    const cr = 245 + (217 - 245) * p.u, cg = 163 + (79 - 163) * p.u, cb = 92 + (31 - 92) * p.u;
    ctx.fillStyle = 'rgba(' + Math.round(cr + (255 - cr) * b * 0.8) + ',' + Math.round(cg + (230 - cg) * b * 0.8) + ','
      + Math.round(cb + (200 - cb) * b * 0.8) + ',' + Math.min(1, 0.7 + 0.3 * b) + ')';
    ctx.beginPath(); ctx.arc(p.x, p.y, p.size * (1 + b * 0.5), 0, TAU); ctx.fill();
  }

  /* 余烬：从字形上随机一点缓缓上飘、渐隐 */
  if (S.embers.length < 16 && Math.random() < 0.24) {
    const s = dots[(Math.random() * dots.length) | 0];
    if (s) S.embers.push({ x: s.x, y: s.y, vx: (Math.random() - 0.5) * 0.2, vy: -(fs / 50) * (0.3 + Math.random() * 0.5), life: 1 });
  }
  S.embers = S.embers.filter((e) => e.life > 0);
  for (const e of S.embers) {
    e.x += e.vx; e.y += e.vy; e.life -= 0.012;
    ctx.fillStyle = 'rgba(245,163,92,' + Math.max(0, e.life * 0.8) + ')';
    ctx.beginPath(); ctx.arc(e.x, e.y, fs / 60, 0, TAU); ctx.fill();
  }
  ctx.globalCompositeOperation = 'source-over';
}
